import {
    Button,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    Typography,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SectionLabel from '~/components/SectionLabel';
import { overwrittenRoiId } from '~/hooks/structure-set/outputTarget';
import { useBooleanOperation } from '~/hooks/structure-set/useBooleanOperation';
import { ICON_PX } from '~/theme/iconSizes';
import { dicomColorToRgbString } from '~/utils/color';
import { WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS } from '../autoPreview';
import {
    ActionButtons,
    getCustomNameError,
    outputActionLabel,
    OutputStructureSelect,
    StructureOption as FormStructureOption,
} from '../OperationFormControls';
import StructureSelect, { StructureSelectOption } from '../StructureSelect';
import { inputLetter } from './booleanOperations';
import FormulaPreview, { FormulaResult } from './FormulaPreview';
import OperationSelect from './OperationSelect';
import StepRow from './StepRow';

interface BooleanOperationContentProps {
    onClose: () => void;
}

export default function BooleanOperationContent({
    onClose,
}: BooleanOperationContentProps) {
    const {
        base,
        selectBase,
        steps,
        addStep,
        removeStep,
        setStepOperation,
        setStepOperand,
        formula,
        resultName,
        setResultName,
        resultTarget,
        setResultTarget,
        structures,
        selectableStructures,
        allRoiNames,
        newStructureColor,
        canPreview,
        isValid,
        buildPayload,
        previewChain,
        clearBooleanPreview,
        executeOperation,
    } = useBooleanOperation();

    const intl = useIntl();
    const [showFormula, setShowFormula] = useState(true);

    // Clears the preview on close so it is not mistaken for an applied result.
    useEffect(() => clearBooleanPreview, [clearBooleanPreview]);

    // Operands offer only unapproved structures: approved ones would fail
    // validation, so they are left out rather than shown disabled.
    const operandOptions: StructureSelectOption[] = useMemo(
        () =>
            selectableStructures.map((s) => ({
                roiId: s.roiId,
                label: s.name,
                color: dicomColorToRgbString(s.color),
            })),
        [selectableStructures]
    );

    const outputStructures: FormStructureOption[] = useMemo(
        () =>
            structures.map((s) => ({
                name: s.name,
                roiId: s.roiId,
                color: s.color,
                // The domain already decides write protection (approval,
                // set approval, lock).
                isWriteProtected: !s.canModifyContours,
            })),
        [structures]
    );

    const customNameError = useMemo(
        () => getCustomNameError(resultName, allRoiNames),
        [resultName, allRoiNames]
    );

    // The preview follows every change to the operation once it can be
    // shown, before the result is named.
    useEffect(() => {
        if (!canPreview) {
            clearBooleanPreview();
            return;
        }
        const timeout = setTimeout(
            previewChain,
            WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS
        );
        return () => {
            clearTimeout(timeout);
        };
    }, [canPreview, previewChain, clearBooleanPreview]);

    const handleCreate = () => {
        const payload = buildPayload();
        if (!payload) {
            return;
        }
        clearBooleanPreview();
        executeOperation(payload);
        onClose();
    };

    const newName = resultName.trim();
    const newColor = dicomColorToRgbString(newStructureColor);
    const actionLabel = outputActionLabel(
        intl,
        resultTarget,
        structures,
        newName || intl.formatMessage({ id: 'booleanOperations.unnamedAction' })
    );
    const targetRoiId = overwrittenRoiId(resultTarget);
    const target = structures.find((s) => s.roiId === targetRoiId);
    const formulaResult: FormulaResult | null =
        resultTarget.kind === 'new'
            ? {
                  name:
                      newName ||
                      intl.formatMessage({ id: 'booleanOperations.unnamed' }),
                  color: newColor,
                  overwrites: false,
              }
            : target
              ? {
                    name: target.name,
                    color: dicomColorToRgbString(target.color),
                    overwrites: true,
                }
              : null;

    const structureLabel = (index: number) =>
        intl.formatMessage(
            { id: 'booleanOperations.structureLetter' },
            { letter: inputLetter(index) }
        );
    const pickStructure = intl.formatMessage({
        id: 'booleanOperations.pickStructure',
    });

    if (structures.length === 0) {
        return (
            <Typography fontSize={12} color="text.secondary">
                Load a structure set first.
            </Typography>
        );
    }

    return (
        <>
            <Stack gap={1}>
                <SectionLabel id="booleanOperations.operations" />
                <StepRow letter={inputLetter(0)}>
                    <StructureSelect
                        value={base?.roiId ?? null}
                        onChange={selectBase}
                        options={operandOptions}
                        aria-label={structureLabel(0)}
                        placeholder={pickStructure}
                    />
                </StepRow>
                {steps.map((step, index) => (
                    <StepRow
                        key={step.id}
                        letter={inputLetter(index + 1)}
                        onRemove={() => removeStep(step.id)}
                        removeLabel={intl.formatMessage({
                            id: 'booleanOperations.removeOperation',
                        })}
                    >
                        <OperationSelect
                            value={step.operation}
                            onChange={(operation) =>
                                setStepOperation(step.id, operation)
                            }
                            inputs={{
                                left:
                                    index === 0
                                        ? inputLetter(0)
                                        : intl.formatMessage({
                                              id: 'booleanOperations.result',
                                          }),
                                right: inputLetter(index + 1),
                            }}
                        />
                        <StructureSelect
                            value={step.operand?.roiId ?? null}
                            onChange={(roiId) => setStepOperand(step.id, roiId)}
                            options={operandOptions}
                            aria-label={structureLabel(index + 1)}
                            placeholder={pickStructure}
                        />
                    </StepRow>
                ))}
                <StepRow>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<Plus size={ICON_PX} />}
                        onClick={addStep}
                    >
                        {intl.formatMessage({
                            id: 'booleanOperations.addOperation',
                        })}
                    </Button>
                </StepRow>
            </Stack>

            <Divider />

            <Stack gap={1}>
                <OutputStructureSelect
                    target={resultTarget}
                    onTargetChange={setResultTarget}
                    inputColor={
                        base ? dicomColorToRgbString(base.color) : 'transparent'
                    }
                    inputRoiId={base?.roiId ?? null}
                    availableStructures={outputStructures}
                    customRoiName={resultName}
                    setCustomRoiName={setResultName}
                    customNameError={customNameError}
                    defaultName={intl.formatMessage({
                        id: 'booleanOperations.namePlaceholder',
                    })}
                    newColor={newColor}
                    modeAction={
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showFormula}
                                    onChange={(_, checked) =>
                                        setShowFormula(checked)
                                    }
                                />
                            }
                            label={intl.formatMessage({
                                id: 'booleanOperations.formula.label',
                            })}
                        />
                    }
                >
                    {showFormula && (
                        <FormulaPreview
                            tokens={formula}
                            result={formulaResult}
                            aria-label={intl.formatMessage({
                                id: 'booleanOperations.formula.label',
                            })}
                        />
                    )}
                </OutputStructureSelect>

                <ActionButtons
                    onApply={handleCreate}
                    isApplyDisabled={!isValid}
                    label={actionLabel}
                    startIcon={null}
                />
            </Stack>
        </>
    );
}
